import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    getTransactionsList,
    getTransactionsIsFetching,
    getTransactionsFetchError,
    getIsLastPage,
    getTotalPendingTransactions,
    getTotalAmountPending,
} from 'lib/selectors';
import useInfiniteTransactionScroll from 'utils/hooks/useInfiniteTransactionScroll';
import AdvertsContainer from '../../adverts/containers/AdvertsContainer';
import PageContainer from 'components/shared/generic/pageContainer/presentational/PageContainer';
import DataCheck from 'components/shared/generic/dataCheck/presentational/DataCheck';
import InfiniteScroll from 'components/shared/infiniteScroll/presentational/InfiniteScroll';
import Transactions from '../presentational/Transactions';
import useTransactions from '../hooks/useTransactions';
import useTranslate from 'lib/languages/useTranslate';
import { getListItems } from '../../../../../utils/transactions';
import { isEmpty } from 'lib/utils';
import Typography from 'components/shared/generic/typography/presentational/Typography';
import Button from 'components/shared/generic/button/presentational/Button';
import useAccountMigrationCheck from 'lib/hooks/useAccountMigrationCheck';
import ActivateCardBanner from '../presentational/ActivateCardBanner';
import { useGetAccountQuery } from 'lib/services';

const TransactionsContainer = () => {
    const token = localStorage.getItem('accessToken');
    const {
        isAccountMigrated,
        selectedAccountCardProvider,
        showPreviousAccountTransactions,
        selectedAccountBankNumber,
        toggleShowPreviousTransactions,
    } = useAccountMigrationCheck(token);

    const { fetchNextPage } = useTransactions(
        isAccountMigrated ? selectedAccountCardProvider : null,
    );

    const transactions = useSelector(getTransactionsList);
    const isLastPage = useSelector(getIsLastPage);
    const isFetchingTransactions = useSelector(getTransactionsIsFetching);
    const transactionsError = useSelector(getTransactionsFetchError);

    const { data: account, error: accountError } = useGetAccountQuery();

    const pendingTransactionsCount = useSelector(getTotalPendingTransactions);
    const pendingTransactionsAmount = useSelector(getTotalAmountPending);
    const translate = useTranslate('transactionsList');

    const ref = useInfiniteTransactionScroll(
        fetchNextPage,
        isFetchingTransactions,
    );

    const formattedTransactions = useMemo(
        () => getListItems(transactions),
        [transactions],
    );

    const items = useMemo(() => {
        let transactions = [...formattedTransactions];

        if (pendingTransactionsCount) {
            const pendingItem = {
                key: 'pending',
                type: 'pending',
                amount: pendingTransactionsAmount,
                count: pendingTransactionsCount,
            };

            transactions = [pendingItem, ...transactions];
        }

        if (!isEmpty(account)) {
            const accountBalanceItem = {
                key: 'accountBalance',
                type: 'accountBalance',
                amount: account?.availableBalance,
            };

            transactions = [accountBalanceItem, ...transactions];
        }

        return transactions;
    }, [
        formattedTransactions,
        pendingTransactionsCount,
        pendingTransactionsAmount,
        account,
    ]);

    const buttonTransactionsProps = {
        text: showPreviousAccountTransactions
            ? translate('buttonTransactionHideText')
            : translate('buttonTransactionShowText'),
        className: 'transaction-toggle-button',
    };

    return (
        <PageContainer className="transactions-container">
            <AdvertsContainer />
            {isAccountMigrated && (
                <>
                    <Button
                        className={buttonTransactionsProps.className}
                        onClick={toggleShowPreviousTransactions}
                    >
                        {buttonTransactionsProps.text}
                    </Button>
                    {showPreviousAccountTransactions && (
                        <>
                            <Typography className="transaction-show-text">
                                {translate('showingTransactionsFrom')}
                            </Typography>
                            <Typography className="transaction-show-text">
                                {selectedAccountBankNumber}
                            </Typography>
                        </>
                    )}
                </>
            )}
            <ActivateCardBanner />
            <DataCheck
                data={items}
                error={transactionsError || accountError}
                noDataMessage={translate('transactionsList')}
                errorMessage={translate('error')}
            >
                <InfiniteScroll
                    className="transactions-scroll-container"
                    ref={ref}
                    last={isLastPage}
                >
                    <Transactions data={items} />
                </InfiniteScroll>
            </DataCheck>
        </PageContainer>
    );
};

export default TransactionsContainer;
